import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  createSocialComment,
  deleteSocialComment,
  fetchSocialComments,
  fetchSocialReactionsSummary,
  toggleSocialReaction,
} from "../api";
import { useAuth } from "./AuthGate";

const parseSummary = (summary) => {
  const like = summary?.reactions?.like || summary?.like || summary || {};
  return {
    count: Number(like?.count ?? summary?.count ?? 0) || 0,
    liked: Boolean(like?.reacted_by_me ?? summary?.reacted_by_me ?? summary?.reacted ?? false),
  };
};

const displayName = (row = {}) =>
  row.display_name || row.author_name || row.username || row.user_name || "User";

const fmtTime = (value) => {
  if (!value) return "";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "";
  return dt.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

export default function SocialEngagement({
  targetType,
  targetId,
  compact = false,
  dark = true,
  initialLikeCount = 0,
  initialLiked = false,
}) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [likeCount, setLikeCount] = useState(Number(initialLikeCount) || 0);
  const [liked, setLiked] = useState(Boolean(initialLiked));
  const [reactionLoading, setReactionLoading] = useState(false);
  const [commentsOpen, setCommentsOpen] = useState(false);
  const [comments, setComments] = useState([]);
  const [commentsLoaded, setCommentsLoaded] = useState(false);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [commentText, setCommentText] = useState("");
  const [commentSaving, setCommentSaving] = useState(false);
  const [err, setErr] = useState("");

  const ready = Boolean(targetType && targetId !== null && targetId !== undefined && targetId !== "");
  const textColor = dark ? "#f8fafc" : "#111827";
  const muted = dark ? "rgba(255,255,255,0.66)" : "rgba(0,0,0,0.58)";
  const border = dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.1)";
  const inputBg = dark ? "rgba(0,0,0,0.35)" : "#fff";

  useEffect(() => {
    if (!ready) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const summary = await fetchSocialReactionsSummary({
          target_type: targetType,
          target_id: targetId,
        });
        if (!cancelled) {
          const parsed = parseSummary(summary);
          setLikeCount(parsed.count);
          setLiked(parsed.liked);
        }
      } catch {
        // Public pages should still render if engagement is unavailable.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [ready, targetType, targetId]);

  const loadComments = async () => {
    if (!ready) return;
    setCommentsLoading(true);
    setErr("");
    try {
      const data = await fetchSocialComments({
        target_type: targetType,
        target_id: targetId,
        limit: compact ? 5 : 50,
      });
      const rows = Array.isArray(data) ? data : data?.comments || [];
      setComments(rows);
      setCommentsLoaded(true);
    } catch {
      setErr("Comments unavailable.");
    } finally {
      setCommentsLoading(false);
    }
  };

  const toggleLike = async () => {
    if (!ready) return;
    if (!user) {
      navigate("/login");
      return;
    }

    const nextLiked = !liked;
    setLiked(nextLiked);
    setLikeCount((prev) => Math.max(0, prev + (nextLiked ? 1 : -1)));
    setReactionLoading(true);
    setErr("");
    try {
      const res = await toggleSocialReaction({
        target_type: targetType,
        target_id: targetId,
        reaction_type: "like",
      });
      const parsed = parseSummary(res);
      setLikeCount(parsed.count);
      setLiked(parsed.liked);
    } catch {
      setLiked(!nextLiked);
      setLikeCount((prev) => Math.max(0, prev + (nextLiked ? -1 : 1)));
      setErr("Could not update like.");
    } finally {
      setReactionLoading(false);
    }
  };

  const toggleComments = async () => {
    const next = !commentsOpen;
    setCommentsOpen(next);
    if (next && !commentsLoaded) await loadComments();
  };

  const submitComment = async () => {
    const body = commentText.trim();
    if (!ready || !body) return;
    if (!user) {
      navigate("/login");
      return;
    }
    setCommentSaving(true);
    setErr("");
    try {
      const created = await createSocialComment({
        target_type: targetType,
        target_id: targetId,
        body,
      });
      setComments((prev) => [created, ...prev]);
      setCommentsLoaded(true);
      setCommentText("");
    } catch {
      setErr("Could not post comment.");
    } finally {
      setCommentSaving(false);
    }
  };

  const removeComment = async (commentId) => {
    if (!commentId) return;
    setErr("");
    try {
      await deleteSocialComment(commentId);
      setComments((prev) => prev.filter((row) => String(row.id) !== String(commentId)));
    } catch {
      setErr("Could not delete comment.");
    }
  };

  if (!ready) return null;

  return (
    <div style={{ marginTop: compact ? 8 : 12, color: textColor }}>
      <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <button
          type="button"
          onClick={toggleLike}
          disabled={reactionLoading}
          style={{
            border: `1px solid ${liked ? "#34d399" : border}`,
            background: liked ? "rgba(52,211,153,0.14)" : "transparent",
            color: liked ? "#34d399" : muted,
            borderRadius: 8,
            padding: compact ? "5px 9px" : "7px 11px",
            fontSize: 12,
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          {liked ? "Liked" : "Like"} {likeCount ? likeCount : ""}
        </button>
        <button
          type="button"
          onClick={toggleComments}
          style={{
            border: `1px solid ${border}`,
            background: "transparent",
            color: muted,
            borderRadius: 8,
            padding: compact ? "5px 9px" : "7px 11px",
            fontSize: 12,
            fontWeight: 800,
            cursor: "pointer",
          }}
        >
          Comment {comments.length ? comments.length : ""}
        </button>
      </div>

      {err ? <div style={{ marginTop: 7, fontSize: 12, color: "#f87171" }}>{err}</div> : null}

      {commentsOpen ? (
        <div style={{ marginTop: 10, borderTop: `1px solid ${border}`, paddingTop: 10 }}>
          <div style={{ display: "flex", gap: 8, marginBottom: 10 }}>
            <input
              value={commentText}
              onChange={(e) => setCommentText(e.target.value)}
              placeholder={user ? "Write a comment..." : "Log in to comment"}
              disabled={!user}
              style={{
                flex: 1,
                minWidth: 0,
                borderRadius: 8,
                border: `1px solid ${border}`,
                background: inputBg,
                color: textColor,
                padding: "8px 10px",
                fontSize: 13,
              }}
            />
            <button
              type="button"
              onClick={submitComment}
              disabled={!user || commentSaving || !commentText.trim()}
              style={{
                border: "1px solid #34d399",
                background: "rgba(52,211,153,0.14)",
                color: "#34d399",
                borderRadius: 8,
                padding: "7px 11px",
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              {commentSaving ? "Posting..." : "Post"}
            </button>
          </div>

          {commentsLoading ? <div style={{ fontSize: 12, color: muted }}>Loading comments...</div> : null}

          <div style={{ display: "grid", gap: 8 }}>
            {comments.map((row, idx) => (
              <div
                key={row.id || `${row.user_id || "u"}-${idx}`}
                style={{
                  border: `1px solid ${border}`,
                  background: dark ? "rgba(0,0,0,0.24)" : "rgba(0,0,0,0.03)",
                  borderRadius: 8,
                  padding: "8px 10px",
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                  <div style={{ fontSize: 12, color: muted }}>
                    {displayName(row)}
                    {row.created_at ? ` - ${fmtTime(row.created_at)}` : ""}
                  </div>
                  {row.can_delete ? (
                    <button
                      type="button"
                      onClick={() => removeComment(row.id)}
                      style={{
                        border: "none",
                        background: "transparent",
                        color: "#f87171",
                        fontSize: 12,
                        cursor: "pointer",
                      }}
                    >
                      Delete
                    </button>
                  ) : null}
                </div>
                <div style={{ marginTop: 4, fontSize: 13, lineHeight: 1.45 }}>
                  {row.body || row.comment || ""}
                </div>
              </div>
            ))}
            {!commentsLoading && commentsLoaded && !comments.length ? (
              <div style={{ fontSize: 12, color: muted }}>No comments yet.</div>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
